import { Alert, Form, Select } from 'antd'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import { useLockQuestionsByFormIdForMeasure } from '@/queries/measures/useLockQuestionsByFormIdForMeasure'
import { useLoadQuestionByResourceId } from '@/queries/questions/useLoadQuestionByResourceId'
import FormAntd from '@/ui/form/antd/FormAntd'
import Modal from '@/ui/Modal'

import {
  ConfirmLockingFormModalModel,
  useConfirmLockingFormModal,
} from './useConfirmLockingFormModal'

type Props = {
  setMeasuresEnabledForm: (value: boolean) => void
}

export const ConfirmLockingFormModal = ({ setMeasuresEnabledForm }: Props) => {
  const params = useParams<{ resourceId?: string }>()
  const updateParams = useUpdateURLSearchParams()
  const { data } = useLoadQuestionByResourceId({
    form: String(params?.resourceId || ''),
  })
  const { form, rule } = useConfirmLockingFormModal()
  const { lockQuestionsByFormIdForMeasure, isLoading } =
    useLockQuestionsByFormIdForMeasure({
      formId: String(params?.resourceId || ''),
      tracked_only: true,
    })

  const questionOptions = data
    ?.filter((item) => ['calculation', 'number'].includes(item.subtype))
    ?.map((item, index) => ({
      label: `${index + 1}. ${item.text}`,
      value: item.id,
    }))

  const onFormSubmit = async (data: ConfirmLockingFormModalModel) => {
    try {
      await lockQuestionsByFormIdForMeasure({
        formId: params?.resourceId || '',
        data,
      })
      toast.info('Form locked successfully')
      updateParams(['locking_form', undefined])
    } catch (error) {
      toast.error('Error while locking a form')
    }
  }

  const onCancel = () => {
    setMeasuresEnabledForm(false)
    updateParams(['locking_form', undefined])
  }
  return (
    <Modal
      open
      maskClosable={false}
      okButtonProps={{ disabled: isLoading, loading: isLoading }}
      okText="Yes, Lock"
      onCancel={onCancel}
      onClose={onCancel}
      onOk={() => form.submit()}
      title="Confirm Locking Form"
    >
      <Alert
        showIcon
        className="my-4"
        description="You are about to lock this form as an assessment. Once locked, it cannot be edited to ensure the data integrity."
        message={<strong>Are you sure you want to proceed?</strong>}
        type="warning"
      />
      <FormAntd.Root<ConfirmLockingFormModalModel>
        className="mb-4 w-full"
        form={form}
        layout="vertical"
        onFinish={onFormSubmit}
        validateTrigger={['onBlur']}
      >
        <Form.Item<ConfirmLockingFormModalModel>
          className="w-full [&_label]:!text-base [&_label]:font-bold"
          label="Select Values to Track"
          name="questions"
          rules={[rule]}
        >
          <Select
            allowClear
            className="w-full [&_.ant-select-selection-wrap]:mt-0.5"
            maxTagCount="responsive"
            mode="multiple"
            options={questionOptions || []}
            placeholder="Select questions"
          />
        </Form.Item>
      </FormAntd.Root>
    </Modal>
  )
}
